// Ops desk modules, row 1: the six summary tiles. Each face reads through the
// hooks in opsModuleHooks, which use the SAME key/queryFn/refetchInterval as the
// panel behind its drawer, so opening the drawer is a cache hit. Faces are
// state-only: no buttons or links inside.
//
// Loading rule (see fleetModuleHooks): a query that is `isLoading` OR
// (`isPaused && data === undefined`) is still LOADING and shows `—` with no
// compare/footer; one that failed with nothing to show gets the error rung; a
// failed REFETCH keeps showing its cached data. An unknown is never a zero.
import type { CloudWatchData, WorkflowRunRow } from '../../api/client'
import { useNow } from '../../hooks/useNow'
import { parseServerTime, relativeTime, untilTime } from '../../lib/relativeTime'
import { PipStrip } from '../viz/PipStrip'
import { Sparkline } from '../viz/Sparkline'
import { StatModule } from './StatModule'
import { CLOCK_MS } from './bookModuleHooks'
import { DASH, RUNS_LIMIT, num, stillLoading, unavailable } from './fleetModuleHooks'
import { runPips } from './fleetSummaries'
import {
  useCloudWatch,
  useEngineStatus,
  useRuntimeAgents,
  useServices,
  useSystemHealth,
  useVetoRates,
  useWorkflowRunsPage1,
} from './opsModuleHooks'
import {
  agentsFace,
  bookPips,
  cloudwatchLatest,
  marketFace,
  NOT_CONFIGURED,
  servicesFace,
  runsWindow,
  vetoRate,
} from './opsSummaries'
import { withMarker } from './statCompare'
import './OpsModules.css'

const CW = 'TradingPlatform/Workflow '
const grouped = (n: number) => n.toLocaleString('en-US')
/** Ruling G: the FailureRate series is one point per day; a newest point older than this is a dead feed. */
const CW_STALE_MS = 36 * 3_600_000

/** Epoch ms of the newest valid point of one CloudWatch metric (parsed, order-independent); null when none. */
function newestPointMs(d: CloudWatchData | undefined, key: string): number | null {
  const m = d?.metrics?.[key]
  if (!m || !Array.isArray(m.timestamps) || !Array.isArray(m.values)) return null
  let newest: number | null = null
  for (let i = 0; i < Math.min(m.timestamps.length, m.values.length); i++) {
    const t = parseServerTime(m.timestamps[i])
    const v = m.values[i]
    if (t === null || typeof v !== 'number' || !Number.isFinite(v)) continue
    if (newest === null || t > newest) newest = t
  }
  return newest
}

const fixed = (v: number | null, dp: number) => (v === null ? DASH : v.toFixed(dp))

/** Duration of the newest run by parsed start time (the API sends newest first, but never trust it). */
function newestDuration(runs: WorkflowRunRow[] | undefined): number | null {
  if (!runs || runs.length === 0) return null
  const newest = runs.reduce((best, r) =>
    (parseServerTime(r.started_at) ?? -Infinity) > (parseServerTime(best.started_at) ?? -Infinity) ? r : best,
  )
  return num(newest.duration_ms)
}

// ------------------------------------------------------------------- engine

export function EngineModule() {
  useNow(CLOCK_MS) // re-read the clock: the countdown must tick on an idle tab
  const q = useEngineStatus()
  const isLoading = stillLoading(q)
  const d = q.data
  const books = bookPips(d?.books)
  const status = typeof d?.status === 'string' && d.status ? d.status : null

  return (
    <StatModule
      title="Engine"
      drawerKey="engine"
      value={status ? status.toUpperCase() : DASH}
      tone={status === null ? 'neutral' : status === 'running' ? 'gain' : 'warn'}
      isLoading={isLoading}
      isError={unavailable(q)}
      errorText="engine unavailable"
      // The countdown only means something while the engine runs; a stopped one has no next run.
      compare={
        d ? (status === 'running' ? `${d.workflow_mode} · next ${untilTime(d.next_workflow_run)}` : d.workflow_mode) : undefined
      }
      footer={
        books.pips.length > 0 ? (
          <PipStrip pips={books.pips} ariaLabel={`${books.pips.length} books, ${books.halted} halted`} />
        ) : undefined
      }
    />
  )
}

// --------------------------------------------------------------------- runs

export function RunsModule() {
  const now = useNow(CLOCK_MS)
  const q = useEngineStatus()
  const runsQ = useWorkflowRunsPage1()
  const isLoading = stillLoading(q)
  const d = q.data
  const runs = runsQ.data?.runs
  const total = num(d?.total_workflow_runs)
  const failed = num(d?.failed_runs)
  const since = runsWindow(total, d?.started_at, now.getTime())
  const last = newestDuration(runs)

  return (
    <StatModule
      title="Runs"
      drawerKey="runs-24h"
      value={total === null ? DASH : grouped(total)}
      tone={failed !== null && failed > 0 ? 'warn' : 'neutral'}
      isLoading={isLoading}
      isError={unavailable(q)}
      errorText="runs unavailable"
      // Ruling 41/F: the counts are since the engine restarted, so the compare names that window.
      // Ruling K: the counters survive an in-container restart, so an implausible total reads `since deploy`.
      // Ruling H: ONE line; a second line plus the pip strip measured 74px in the 72px body, so
      // the last run's duration rides in the strip's aria-label instead.
      compare={d ? `${failed === null ? DASH : failed} failed · since ${since ?? DASH}` : undefined}
      footer={
        runs ? (
          <PipStrip
            pips={runPips(runs, RUNS_LIMIT)}
            ariaLabel={`recent workflow runs, last ${last === null ? DASH : `${last} ms`}`}
          />
        ) : undefined
      }
    />
  )
}

// ----------------------------------------------------------------- services

export function ServicesModule() {
  const q = useServices()
  const isLoading = stillLoading(q)
  const face = servicesFace(q.data)
  const allUp = face !== null && face.up === face.total

  return (
    <StatModule
      title="Services"
      drawerKey="services"
      value={face ? `${face.up}/${face.total}` : DASH}
      tone={face === null ? 'neutral' : allUp ? 'gain' : 'warn'}
      isLoading={isLoading}
      isError={unavailable(q)}
      errorText="services unavailable"
      isEmpty={q.data !== undefined && face === null}
      emptyText="no services reported"
      // A count, not names: names wrap to 3 lines in the 72px body and push the pips out of view.
      compare={face ? (allUp ? 'all circuits closed' : `${face.down.length} down`) : undefined}
      footer={
        face ? (
          <PipStrip
            pips={face.pips}
            ariaLabel={`${face.up} of ${face.total} services up${allUp ? '' : `, down: ${face.down.join(', ')}`}`}
          />
        ) : undefined
      }
    />
  )
}

// --------------------------------------------------------------- cloudwatch

export function CloudWatchModule() {
  const now = useNow(CLOCK_MS)
  const q = useCloudWatch()
  const isLoading = stillLoading(q)
  const failure = cloudwatchLatest(q.data, `${CW}FailureRate`)
  const perHour = cloudwatchLatest(q.data, `${CW}RunsPerHour`)
  const duration = cloudwatchLatest(q.data, `${CW}AvgDurationMs`)
  const notAvailable = q.data?.available === false
  const newest = newestPointMs(q.data, `${CW}FailureRate`)
  const stale = failure !== null && newest !== null && now.getTime() - newest > CW_STALE_MS

  return (
    <StatModule
      title="CloudWatch"
      drawerKey="cloudwatch"
      value={failure ? `${failure.value.toFixed(2)}% fail` : DASH}
      isLoading={isLoading}
      isError={unavailable(q)}
      errorText="cloudwatch unavailable"
      isEmpty={notAvailable}
      emptyText="not available here"
      compare={
        q.data && !notAvailable
          ? withMarker(
              `${fixed(perHour?.value ?? null, 1)}/h · ${fixed(duration?.value ?? null, 0)} ms`,
              stale ? 'stale' : null,
            )
          : undefined
      }
      footer={
        failure && failure.series.length >= 2 ? (
          <Sparkline
            values={failure.series}
            height={20}
            ariaLabel={`Daily workflow failure rate trend, latest ${failure.value.toFixed(2)}%`}
          />
        ) : undefined
      }
    />
  )
}

// ------------------------------------------------------------------- agents

export function AgentsModule() {
  const q = useRuntimeAgents()
  const vetoQ = useVetoRates()
  const isLoading = stillLoading(q)
  const face = agentsFace(q.data)
  const veto = vetoRate(vetoQ.data)
  // The orchestrator is absent: every agent is a placeholder, so the veto rate is moot.
  const allUnconfigured = face !== null && face.notConfigured === face.total
  const vetoText =
    vetoQ.data === undefined
      ? `veto ${DASH} · 7d`
      : veto === null
        ? 'no gate events · 7d'
        : `veto ${(veto * 100).toFixed(1)}% · 7d`

  return (
    <StatModule
      title="Agents"
      drawerKey="agents"
      value={face ? `${face.healthy}/${face.total}` : DASH}
      tone={
        face === null || face.notConfigured === face.total
          ? 'neutral'
          : face.healthy === face.total - face.notConfigured
            ? 'gain'
            : 'warn'
      }
      isLoading={isLoading}
      isError={unavailable(q)}
      errorText="agents unavailable"
      isEmpty={q.data !== undefined && face === null}
      emptyText="no agents reported"
      compare={face ? (allUnconfigured ? NOT_CONFIGURED : vetoText) : undefined}
      footer={
        face ? (
          <PipStrip
            pips={face.pips}
            ariaLabel={`${face.healthy} of ${face.total} agents healthy${allUnconfigured ? `, ${NOT_CONFIGURED}` : ''}`}
          />
        ) : undefined
      }
    />
  )
}

// ------------------------------------------------------------------- market

export function MarketModule() {
  const now = useNow(CLOCK_MS)
  const q = useSystemHealth()
  const isLoading = stillLoading(q)
  const d = q.data
  const face = marketFace(d, now.getTime())

  return (
    <StatModule
      title="Market"
      drawerKey="market"
      value={face ? face.word : DASH}
      isLoading={isLoading}
      isError={unavailable(q)}
      errorText="market unavailable"
      compare={
        d && face ? (
          <>
            <span className="ops-compare-line">
              {num(d.pending_signals) ?? DASH} pending · {num(d.active_positions) ?? DASH} held
            </span>
            <span className="ops-compare-line">
              {withMarker(`updated ${relativeTime(d.last_updated)}`, face.stale ? 'stale' : null)}
            </span>
          </>
        ) : undefined
      }
    />
  )
}
